import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { first, isEmpty, remove } from 'lodash';
import { Observable, from, of } from 'rxjs';
import { BusinessComplianceSetting } from 'src/app/classes/models/business-compliance-setting';
import { ComplianceSettingsAndFindingsResults } from 'src/app/interfaces/compliance-settings-and-findings-results';
import { ComplianceResultStatuses } from 'src/app/interfaces/compliance/compliance-result-statuses';
import { BusinessInfoComplianceResults } from 'src/app/interfaces/compliance/results/business-info/business-info-compliance-results';
import { NewBusinessInfoComplianceResults } from 'src/app/interfaces/compliance/results/business-info/new-business-info-compliance-results';
import { InsurancePolicyTypeAbbreviated } from 'src/app/interfaces/insurance-policy-type-abbreviation';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { RegistrationStepName } from 'src/app/interfaces/registration-step-name';
import { ViveStatuses } from 'src/app/interfaces/vive-statuses';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { CompanyService } from 'src/app/services/company.service';
import { ComplianceService } from 'src/app/services/compliance.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { VendorService } from 'src/app/services/vendor.service';
import { spotlightReportsService } from 'src/app/shared/vive-api/spotlight-reports.service';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';

import {
  Address,
  ClientRegisteredVendor,
  CompanyVendor,
  ComplianceSetting,
  Contact,
  ExemptVendor,
  ExemptVendorType,
  InsuranceCertificate,
  InsurancePolicy,
  User,
  Vendor,
  ViveProgress
} from '../../../../classes/models';

/**
 * VPDW-4323: the client-facing payment standing for one CompanyVendor.
 * Exported so the spec can assert against the union rather than bare strings.
 *
 * `unconfirmed` and `noRecord` both render as the neutral "Confirming" chip but
 * are kept apart because they carry different copy: `noRecord` means we hold no
 * payment at all, `unconfirmed` means the ledger and the relationship disagree.
 */
export type PaymentStanding =
  | 'registration'
  | 'current'
  | 'renewsSoon'
  | 'lapsed'
  | 'feeWaived'
  | 'noRecord'
  | 'unconfirmed';

/** Which explanation line the payment card renders, if any. */
export type PaymentExplanation =
  | 'suspendedForPayment'
  | 'suspendedForVendorInfo'
  | 'overdue'
  | 'feeWaived'
  | 'noRecord'
  | 'unconfirmed';

@Component({
  selector: 'app-client-my-vendor',
  templateUrl: './client-my-vendor.component.html',
  styleUrls: ['./client-my-vendor.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientMyVendor implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private CompanyVendorService: CompanyVendorService,
    private ToastService: ToastService,
    private UtilityService: UtilityService,
    private ComplianceService: ComplianceService,
    private AuthService: AuthService,
    private VendorService: VendorService,
    private CompanyService: CompanyService
  ) {
    this.LoggedInUser = this.AuthService.getUser();
  }

  LoggedInUser: User;

  VendorId: number | null = null;
  ClientRegisteredVendorId: number | null = null;
  ExemptVendorId: number | null = null;
  CompanyId!: number;
  CompanyVendor!: CompanyVendor;
  Vendor!: Vendor | ClientRegisteredVendor | ExemptVendor;
  primaryContact?: Contact;
  localizationInfo!: LocalizationInfo;
  tradesConcatenated!: string;
  preferredStatus!: string;
  usesSpotlightVendors = false;
  hasComplianceTypes = false;
  usesRiskTiers = false;
  logoUrl?: string;

  BusinessInfoResults?: BusinessInfoComplianceResults;
  NewBusinessInfoResults?: NewBusinessInfoComplianceResults;
  BusinessComplianceSetting?: BusinessComplianceSetting;
  ComplianceSetting?: ComplianceSetting;

  complianceSettings!: Observable<any>;
  complianceFindings!: Observable<any>;
  settingsAndFindingsArr: Observable<any>[] = [];
  settingsAndFindingsResults!: ComplianceSettingsAndFindingsResults;
  // settingsAndFindingsResults?: { settings: ComplianceSetting, findings: BusinessInfoComplianceResults | NewBusinessInfoComplianceResults };

  compVendors!: any;
  generalLiabilities: {
    title: string;
    finding: any;
    setting: any;
    result: any;
    enabled: boolean;
  }[] = [];
  umbrellaLiabilities: {
    title: string;
    finding: any;
    setting: any;
    result: any;
    enabled: boolean;
  }[] = [];
  autoLiabilities: {
    title: string;
    finding: any;
    setting: any;
    result: any;
    enabled: boolean;
  }[] = [];
  workersComp: {
    title: string;
    finding: any;
    setting: any;
    result: any;
    enabled: boolean;
  }[] = [];
  professionalLiabilities: {
    title: string;
    finding: any;
    setting: any;
    result: any;
    enabled: boolean;
  }[] = [];
  registrationType!: 'registration' | 'renewal';
  excludedRegistrationSteps = new Set<RegistrationStepName>();
  forceCompleteRegistrationSteps = new Set<RegistrationStepName>();

  // Used for displaying trade/license pass/fail points.
  TradeLicenseComplianceProgress?: ViveProgress;

  InsuranceCertificateFindingResults?: {
    [key: number]: {
      viveNamedCertHolder: ComplianceResultStatuses;
      agentSigned: ComplianceResultStatuses;
    };
  };
  InsuranceCertificatesMappedById = new Map<number, InsuranceCertificate>([]);

  InsurancePolicyMap = new Map<
    InsurancePolicyTypeAbbreviated,
    null | InsurancePolicy
  >([
    ['GL', null],
    ['AUTO', null],
    ['UMB', null],
    ['WC', null],
    ['PL', null]
  ]);

  watchlistValues: { label: string; value: number | null }[] = [
    { label: '', value: null },
    { label: 'No Watchlists', value: 0 },
    { label: 'Watchlist Found', value: 1 }
  ];

  bankruptcyValues: { label: string; value: number | null }[] = [
    { label: '', value: null },
    { label: 'No Bankruptcies', value: 0 },
    { label: 'Forgiven Bankruptcies', value: -1 },
    { label: '1 YPD', value: 1 },
    { label: '2 YPD', value: 2 },
    { label: '3 YPD', value: 3 },
    { label: '4 YPD', value: 4 },
    { label: '5 YPD', value: 5 },
    { label: '6 YPD', value: 6 },
    { label: '7 YPD', value: 7 },
    { label: '8+ YPD', value: 8 }
  ];

  vendorTradeStatesConcatenated: string = '';

  // For Compliance Exempt vendors, the type name lives on the ExemptVendorType
  // record (set on the exempt vendor edit page) rather than CompanyComplianceType.
  exemptVendorTypeName: string = '';

  getWatchlistValue(watchlist: number | null): string {
    const found = this.watchlistValues.find((w) => w.value === watchlist);
    return found ? found.label : '';
  }

  getBankruptcyValue(bankruptcy: number | null): string {
    const found = this.bankruptcyValues.find((b) => b.value === bankruptcy);
    return found ? found.label : '';
  }

  formatAddress(Address: Address) {
    const AddressParts = [];
    const { address1, address2, city, state, zip } = Address;

    if (address1) {
      AddressParts.push(address1);
    }

    if (address2) {
      AddressParts.push(address2);
    }

    if (city) {
      AddressParts.push(city + ',');
    }

    if (state) {
      AddressParts.push(state + ',');
    }

    if (zip) {
      AddressParts.push(zip);
    }

    return AddressParts.join(' ');
  }

  canViewVendorTierComplianceType() {
    const acceptedRoles = ['ADMIN_ADMIN', 'CLIENT_ADMIN'];

    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    if (role && new Set(acceptedRoles).has(role)) {
      return true;
    }

    return false;
  }

  // The 1099 route denies CLIENT_VENDOR_AND_SITES_MANAGER, so the row is hidden
  // for it rather than shown as a checkbox whose save returns 403.
  isVendorAndSitesManager(): boolean {
    return (
      this.LoggedInUser.UserAccountRole?.role ===
      'CLIENT_VENDOR_AND_SITES_MANAGER'
    );
  }

  canShowTier(): boolean {
    return (
      this.canViewVendorTierComplianceType() &&
      this.usesRiskTiers &&
      !!this.CompanyVendor?.vendorRiskLevel
    );
  }

  canShowComplianceType(): boolean {
    return (
      this.canViewVendorTierComplianceType() &&
      this.hasComplianceTypes &&
      !!this.complianceTypeDisplay()
    );
  }

  shouldShowRegistrationProgress(): boolean {
    const { viveStatus } = this.CompanyVendor || {};
    return viveStatus === 'Registering' || viveStatus === 'Awaiting Payment';
  }

  // ==========================================================================
  // VPDW-4323 — payment standing and the two renewal clocks, for the CLIENT.
  //
  // Everything below is read-only derivation over fields already on the
  // CompanyVendor payload. Two rules shape it:
  //
  //  1. The client sees DATES AND STANDING, never an amount. A vendor's fee is
  //     between the vendor and VIVE, so no amount is requested (see the
  //     RecentCompanyVendorPayment attribute list in ngOnInit) and none is
  //     rendered.
  //  2. When the ledger and the relationship disagree, the card says nothing
  //     rather than something wrong. That failure mode is real and recent —
  //     VPDW-4077 / VPDW-3618 lost `invoice.paid` ledger writes to a deadlock,
  //     VPDW-4149 left stale EXPIRED rows on vendors who were current — and
  //     this is the first surface that shows the ledger to an external
  //     audience, so a defect here becomes a client escalation. Ambiguity
  //     resolves to the neutral state, never to "lapsed".
  // ==========================================================================

  /**
   * 30 days. Two independent reasons for this number, both from the backend:
   * it is the first pre-expiry rung of the `expStatus` ladder the fee-renewal
   * cron walks (`30D`, then 15D / 10D / 5D / 1D / EXPIRED), and it is wider
   * than the widest renewal reminder tier — `VendorExpiringSoonSeriesStrategy`
   * fires at -28 / -21 / -14 / -7 days. Taking the wider of the two means the
   * client sees "renews soon" no later than the day the vendor gets their
   * first reminder, rather than on a threshold invented for the UI.
   */
  private static readonly PAYMENT_RENEWS_SOON_DAYS = 30;

  /**
   * 60 days, carried over unchanged from the staff card (VPDW-4240): the
   * widest of the backend's information-renewal reminder tiers, which fire at
   * 60 / 30 / 7 days before the due date.
   */
  private static readonly INFO_RENEWAL_DUE_SOON_DAYS = 60;

  /**
   * Statuses that mean this relationship has not finished registering for this
   * client. Deliberately NOT folded into `shouldShowRegistrationProgress()`
   * above, even though the sets nearly match: that method also decides whether
   * `app-registration-tracker` renders and whether the compliance column
   * narrows to `col-md-8`, so adding `Unregistered` to it would change layout
   * behavior this ticket has no business changing.
   */
  private static readonly IN_REGISTRATION_STATUSES: ReadonlySet<ViveStatuses> =
    new Set<ViveStatuses>(['Unregistered', 'Registering', 'Awaiting Payment']);

  /**
   * Whole days from today until `date`; negative once it is past. Null when
   * there is no date.
   *
   * Compares CALENDAR DAYS, not instants — otherwise a renewal falling later
   * today reads as already lapsed, and "renews in 1 day" flickers by
   * wall-clock time. Same approach as the staff card so the two agree.
   */
  private daysUntil(date: Date | null | undefined): number | null {
    if (!date) {
      return null;
    }

    const target = new Date(date);
    const startOfTarget = new Date(
      target.getFullYear(),
      target.getMonth(),
      target.getDate()
    );
    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );

    return Math.round(
      (startOfTarget.getTime() - startOfToday.getTime()) / 86400000
    );
  }

  public get paymentDaysUntilRenewal(): number | null {
    return this.daysUntil(this.CompanyVendor?.renewalDate);
  }

  public get infoRenewalDaysUntilDue(): number | null {
    return this.daysUntil(this.CompanyVendor?.infoRenewalDueDate);
  }

  public get isSuspendedForPayment(): boolean {
    const { viveStatus, viveSubStatus } = this.CompanyVendor || {};
    return (
      viveStatus === 'Suspended' && viveSubStatus === 'No Renewal - Payment'
    );
  }

  public get isSuspendedForVendorInfo(): boolean {
    const { viveStatus, viveSubStatus } = this.CompanyVendor || {};
    return (
      viveStatus === 'Suspended' && viveSubStatus === 'No Renewal - Vendor Info'
    );
  }

  private get isFeeWaivedRelationship(): boolean {
    const { feeWaived, needsFeeWaiveRenewal } = this.CompanyVendor || {};
    return !!feeWaived || !!needsFeeWaiveRenewal;
  }

  /**
   * The card's single source of truth. Evaluated in precedence order; the
   * first match wins.
   */
  public get paymentStanding(): PaymentStanding {
    const CompanyVendor = this.CompanyVendor;

    if (!CompanyVendor) {
      return 'unconfirmed';
    }

    const { viveStatus, vendorPaid, vendorLastDatePaid, renewalDate } =
      CompanyVendor;

    // AC5 — this story is explicitly about the POST-registration view.
    if (viveStatus && ClientMyVendor.IN_REGISTRATION_STATUSES.has(viveStatus)) {
      return 'registration';
    }

    // AC4 — a `No Renewal - Payment` suspension is not the ambiguity AC9
    // guards against. The platform has already ruled on this relationship, so
    // report it, and do it BEFORE the date maths so a stale or missing
    // `renewalDate` cannot soften a suspension into "confirming".
    if (this.isSuspendedForPayment) {
      return 'lapsed';
    }

    // AC3 — a fee-waived relationship legitimately never pays. Say so
    // explicitly instead of falling through to an empty state that reads as
    // "never paid".
    if (this.isFeeWaivedRelationship) {
      return 'feeWaived';
    }

    // AC8 — nothing on record at all. Note this is NOT the same as "not paid":
    // a lost ledger write leaves a genuinely paid vendor looking exactly like
    // this, which is why the copy is neutral.
    if (!vendorLastDatePaid && !renewalDate) {
      return 'noRecord';
    }

    const daysUntilRenewal = this.paymentDaysUntilRenewal;

    // A last-paid date but no paid-through date: we know money arrived and
    // cannot say what it covers. Neutral, per AC9.
    if (daysUntilRenewal === null) {
      return 'unconfirmed';
    }

    if (daysUntilRenewal >= 0) {
      return daysUntilRenewal <= ClientMyVendor.PAYMENT_RENEWS_SOON_DAYS
        ? 'renewsSoon'
        : 'current';
    }

    // AC9 — paid-through is in the past, so assert a lapse ONLY when a second
    // source agrees: either the current payment row has reached EXPIRED on the
    // ladder, or the relationship itself no longer reads as paid. If the two
    // disagree (past paid-through, but `vendorPaid` true and the payment row
    // still on a live rung — the VPDW-4149 / VPDW-4097 shape) we cannot
    // support "lapsed", so we do not claim it.
    const expStatus = this.CompanyVendor?.RecentCompanyVendorPayment?.expStatus;

    if (expStatus === 'EXPIRED' || vendorPaid !== true) {
      return 'lapsed';
    }

    return 'unconfirmed';
  }

  public get paymentStandingLabel(): string {
    const standing = this.paymentStanding;

    if (standing === 'renewsSoon') {
      const days = this.paymentDaysUntilRenewal ?? 0;

      if (days === 0) {
        return 'Renews today';
      }

      return `Renews in ${days} ${days === 1 ? 'day' : 'days'}`;
    }

    if (standing === 'lapsed') {
      const days = this.paymentDaysUntilRenewal;

      // A payment suspension with no usable paid-through date still has to
      // read as lapsed — just without a day count it cannot substantiate.
      if (days === null || days >= 0) {
        return 'Lapsed';
      }

      const overdueBy = Math.abs(days);

      return `Lapsed ${overdueBy} ${overdueBy === 1 ? 'day' : 'days'} ago`;
    }

    return {
      current: 'Current',
      feeWaived: 'Fee waived',
      noRecord: 'Confirming',
      unconfirmed: 'Confirming',
      registration: 'Registration in progress'
    }[standing];
  }

  /**
   * Chip class for the standing, or null when it should not be pilled at all.
   *
   * `current` returns null on purpose: docs/design-components.md says not to
   * pill a passing state where it would over-emphasize. The template renders
   * the V-mark glyph and plain text instead, which still satisfies AC2's
   * "plain-language standing indicator" without the card shouting on the
   * common case.
   */
  public get paymentStandingChipClass(): string | null {
    return {
      current: null,
      renewsSoon: 'chip-warning',
      lapsed: 'chip-error',
      feeWaived: 'chip-info',
      noRecord: 'chip-neutral',
      unconfirmed: 'chip-neutral',
      registration: null
    }[this.paymentStanding];
  }

  /**
   * Which explanation line to render under the rows, if any. The copy itself
   * lives in the template so the dates go through the `date` pipe rather than
   * being string-concatenated here.
   */
  public get paymentExplanation(): PaymentExplanation | null {
    const standing = this.paymentStanding;

    if (this.isSuspendedForVendorInfo) {
      return 'suspendedForVendorInfo';
    }

    if (standing === 'lapsed') {
      return this.isSuspendedForPayment ? 'suspendedForPayment' : 'overdue';
    }

    if (standing === 'feeWaived') {
      return 'feeWaived';
    }

    if (standing === 'noRecord') {
      return 'noRecord';
    }

    if (standing === 'unconfirmed') {
      return 'unconfirmed';
    }

    return null;
  }

  /**
   * Chip text for the information-renewal row, or null when the vendor is
   * comfortably current — same "don't pill the passing state" rule as the
   * standing row above.
   */
  public get infoRenewalChipLabel(): string | null {
    const days = this.infoRenewalDaysUntilDue;

    if (days === null) {
      return null;
    }

    if (days < 0) {
      const overdueBy = Math.abs(days);

      return `Overdue ${overdueBy} ${overdueBy === 1 ? 'day' : 'days'}`;
    }

    if (days === 0) {
      return 'Due today';
    }

    if (days <= ClientMyVendor.INFO_RENEWAL_DUE_SOON_DAYS) {
      return `Due in ${days} ${days === 1 ? 'day' : 'days'}`;
    }

    return null;
  }

  public get infoRenewalChipClass(): string {
    const days = this.infoRenewalDaysUntilDue;

    return days !== null && days < 0 ? 'chip-error' : 'chip-warning';
  }

  /**
   * Only a real `Vendor` relationship has these clocks — an `ExemptVendor` or
   * `ClientRegisteredVendor` pays nothing and has no annual profile update.
   * Matches the gate already on the renewal date this card takes over.
   */
  public get shouldShowPaymentCard(): boolean {
    return !!this.VendorId && !!this.CompanyVendor;
  }

  formatOfficePhone(phone: string | null | undefined): string {
    if (!phone) {
      return '';
    }

    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return `${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
    }

    return phone;
  }

  private getCertificateFindingRowsForPolicy(
    policyType: InsurancePolicyTypeAbbreviated
  ): {
    title: string;
    finding: any;
    setting: any;
    result: any;
    enabled: boolean;
  }[] {
    if (!this.InsuranceCertificateFindingResults) {
      return [];
    }

    for (const [
      certId,
      certificate
    ] of this.InsuranceCertificatesMappedById.entries()) {
      const policies = certificate.InsurancePolicies || [];
      if (!policies.some((policy) => policy.policyType === policyType)) {
        continue;
      }

      const findings = this.InsuranceCertificateFindingResults[certId];
      if (!findings) {
        continue;
      }

      return [
        {
          title: 'Agent Signed?',
          finding: certificate.agentSigned ? 'Yes' : 'No',
          setting: 'Required',
          result: findings.agentSigned,
          enabled: true
        },
        {
          title: 'VIVE Named Cert Holder?',
          finding: certificate.viveNamedCertHolder ? 'Yes' : 'No',
          setting: 'Required',
          result: findings.viveNamedCertHolder,
          enabled: true
        }
      ];
    }

    return [];
  }

  private appendCertificateFindingsToPolicies(): void {
    this.generalLiabilities = [
      ...this.generalLiabilities,
      ...this.getCertificateFindingRowsForPolicy('GL')
    ];
    this.autoLiabilities = [
      ...this.autoLiabilities,
      ...this.getCertificateFindingRowsForPolicy('AUTO')
    ];
    this.umbrellaLiabilities = [
      ...this.umbrellaLiabilities,
      ...this.getCertificateFindingRowsForPolicy('UMB')
    ];
    this.workersComp = [
      ...this.workersComp,
      ...this.getCertificateFindingRowsForPolicy('WC')
    ];
    this.professionalLiabilities = [
      ...this.professionalLiabilities,
      ...this.getCertificateFindingRowsForPolicy('PL')
    ];
  }

  complianceTypeDisplay(): string {
    return (
      this.exemptVendorTypeName ||
      this.CompanyVendor.CompanyComplianceType?.type ||
      ''
    );
  }

  setExemptVendorTypeName(ExemptVendorTypeId: number) {
    this.CompanyService.getCompanyExemptVendorTypes().subscribe({
      next: (ExemptVendorTypes: ExemptVendorType[]) => {
        const ExemptVendorTypeRecord = (ExemptVendorTypes || []).find(
          (exemptVendorType) => exemptVendorType.id === ExemptVendorTypeId
        );

        if (ExemptVendorTypeRecord?.type) {
          this.exemptVendorTypeName = ExemptVendorTypeRecord.type;
          this._cdr.markForCheck();
        }
      }
    });
  }

  setCompanyVendor(CompanyVendor: CompanyVendor) {
    Object.assign(this.CompanyVendor, CompanyVendor);
  }

  isVendor(vendor: Vendor | ExemptVendor | ClientRegisteredVendor) {
    return vendor instanceof Vendor;
  }

  vendorType(vendor: Vendor | ExemptVendor | ClientRegisteredVendor) {
    if (vendor instanceof Vendor) {
      return 'Vendor';
    }
    if (vendor instanceof ClientRegisteredVendor) {
      return 'ClientRegisteredVendor';
    }
    if (vendor instanceof ExemptVendor) {
      return 'ExemptVendor';
    }
    throw new Error('Unknown vendor type');
  }

  getIssue1099Display() {
    const vendorTypeValue = this.vendorType(this.Vendor);
    if (
      vendorTypeValue === 'ClientRegisteredVendor' &&
      'issue1099' in this.Vendor
    ) {
      return !this.Vendor.issue1099 ? 'NO' : 'YES';
    }
    if (vendorTypeValue === 'ExemptVendor' && 'is1099Issued' in this.Vendor) {
      return !this.Vendor.is1099Issued ? 'NO' : 'YES';
    }
    return '';
  }

  saveCompanyVendor() {
    const CompanyVendorData = {
      id: this.CompanyVendor.id,
      VendorId: this.CompanyVendor.VendorId,
      CompanyId: this.CompanyVendor.CompanyId,
      issue1099: this.CompanyVendor.issue1099
    };
    return this.CompanyVendorService.saveCompanyVendor(
      CompanyVendorData
    ).subscribe({
      next: (savedCompanyVendor) => {
        this.CompanyVendor.issue1099 = savedCompanyVendor.issue1099;
        this.ToastService.addSuccessToast(
          'Success',
          '1099 preference updated successfully.',
          { autohide: true, delay: 3000 }
        );
      }
    });
  }

  editVendor() {
    if (!this.ClientRegisteredVendorId && !this.ExemptVendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot edit vendor. No vendor ID found.',
        { returnError: true }
      );
    }

    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot edit vendor. No Company ID found.',
        { returnError: true }
      );
    }

    if (this.ClientRegisteredVendorId) {
      this.router.navigate([
        'client',
        this.CompanyId,
        'vendors',
        'client-registered-vendor',
        this.ClientRegisteredVendorId,
        'edit'
      ]);
    }

    if (this.ExemptVendorId) {
      this.router.navigate([
        'client',
        this.CompanyId,
        'vendors',
        'exempt-vendor',
        this.ExemptVendorId,
        'edit'
      ]);
    }
  }

  checkIfRequired(result: any) {
    return !!result && result !== 'NOT REQUIRED';
  }

  recalculateComplianceResults(ComplianceResults: any) {
    const BusinessInfoProperties = [
      'companyIdentity',
      'companyInGoodStanding',
      'companyLiens',
      'companyWatchlist',
      'bankruptcy',
      'w9Check'
    ];

    this.BusinessInfoResults =
      this.UtilityService.constructObjectFromObjectProperties(
        ComplianceResults,
        BusinessInfoProperties
      ) as BusinessInfoComplianceResults;

    const NewBusinessInfoProperties = [
      'addressResults',
      'bankruptcyResults',
      'businessVerifiedResults',
      'governmentWatchlistResults',
      'lienResults',
      'secretaryOfStateResults',
      'requesterResults',
      'w9Results'
    ];

    this.NewBusinessInfoResults =
      this.UtilityService.constructObjectFromObjectProperties(
        ComplianceResults,
        NewBusinessInfoProperties
      ) as NewBusinessInfoComplianceResults;
  }

  isInsuranceCertificateFindingResults() {
    return !isEmpty(this.InsuranceCertificateFindingResults);
  }

  /**
   * VPDW-2577 follow-up: Compliance Details trusts backend compliance results
   * (via settings + this helper) instead of re-deriving visibility from
   * client-side trade flags like isProfessionalOnly. Hide a policy only when
   * every enabled finding is NOT REQUIRED.
   */
  hasRequiredResults(
    InsurancePolicySettingsResults: {
      title: string;
      finding: any;
      setting: any;
      result: any;
      enabled: boolean;
    }[]
  ) {
    const enabledResults = InsurancePolicySettingsResults.filter(
      (ipsr) => ipsr.enabled
    );

    if (enabledResults.every((ipsr) => ipsr.result === 'NOT REQUIRED')) {
      return false;
    }

    return true;
  }

  ngOnInit(): void {
    const paramMap: ParamMap = this.route.snapshot.paramMap;
    const CompanyId = this.AuthService.getUserCompanyId();
    const VendorId = Number(paramMap.get('VendorId')) || null;
    const ExemptVendorId = Number(paramMap.get('ExemptVendorId')) || null;
    const ClientRegisteredVendorId =
      Number(paramMap.get('ClientRegisteredVendorId')) || null;

    (() => {
      if (VendorId) {
        return this.ComplianceService.getTierLevelForVendor(
          CompanyId,
          VendorId
        );
      }

      return of(null);
    })().subscribe({
      next: (riskLevel) => {
        const companyVendorQueryObj = {
          aliasedAttributes: [
            'registrationType',
            'isOwnerInfoRequired',
            'vendorRiskLevel',
            'renewalDate',
            // VPDW-4323: the vendor-level information-renewal due date, asked for
            // alongside the payment `renewalDate` above because the payment card
            // shows both clocks. Registered on the CompanyVendor mirror class by
            // VPDW-4240; an unregistered alias is dropped silently on hydration,
            // so this string is the whole wiring.
            'infoRenewalDueDate',
            'vendorApprovedTradeStates'
          ],
          // VPDW-4323: the current non-REPLACED payment row for this
          // relationship, read ONLY to corroborate a lapse before the card
          // asserts one (see `paymentStanding`).
          //
          // The attribute list is load-bearing, not tidiness: CompanyVendorPayment
          // carries feeAmt / paidAmt / remittanceAmt / discountAmt, and a client
          // must never see a vendor's fee. Naming the three fields we use keeps
          // money out of the response payload entirely rather than trusting the
          // template not to render it.
          //
          // `required: false` because a relationship with no payment row at all
          // is an ordinary state the page still has to load.
          RecentCompanyVendorPayment: {
            options: { required: false },
            attributes: ['id', 'expDate', 'expStatus']
          },
          WSIB: {},
          WCB: {},
          CompanyComplianceType: {},
          Company: {
            options: { required: true },
            attributes: [
              'id',
              'legalName',
              'fullServiceOffsite',
              'fullServiceOnsite',
              'hasGlobalCompliance'
            ],
            ComplianceSetting: {
              options: { required: true, separate: true },
              ...(riskLevel !== null && { riskLevel })
            },
            BusinessComplianceSetting: {
              options: { required: false }
            },
            CompanyComplianceType: {
              options: { required: false, separate: true },
              attributes: ['id', 'type']
            },
            ApConfig: {
              options: { required: false },
              attributes: ['id', 'apSystem']
            }
          },
          InsuranceCertificate: {
            options: { separate: true },
            status: { $notIn: ['Archived', 'Replaced'] },
            InsurancePolicy: {
              options: { required: true }
            }
          },
          OwnerComplianceProgress: {},
          TradeLicenseComplianceProgress: {}
        };

        const vendorIncludeObj = {
          excludedAttributes: ['logo'],
          StreetAddress: {},
          MailingAddress: {},
          RemitToAddress: {},
          Contact: {},
          VendorTrade: {
            PrimaryTrade: {
              TradeCategory: {}
            }
          },
          VendorRanking: {
            CompanyServiceRanking: {}
          }
        };

        this.CompanyId = CompanyId;

        if (ClientRegisteredVendorId) {
          this.ClientRegisteredVendorId = ClientRegisteredVendorId;
          Object.assign(companyVendorQueryObj, {
            ClientRegisteredVendorId,
            ClientRegisteredVendor: vendorIncludeObj
          });
        }

        if (ExemptVendorId) {
          this.ExemptVendorId = ExemptVendorId;
          Object.assign(companyVendorQueryObj, {
            ExemptVendorId,
            ExemptVendor: vendorIncludeObj
          });
        }

        if (VendorId) {
          this.VendorId = VendorId;
          Object.assign(companyVendorQueryObj, {
            VendorId,
            Vendor: Object.assign(vendorIncludeObj, {
              InsuranceAgent: {}
            })
          });
        }

        this.CompanyVendorService.getCompanyVendor(
          this.UtilityService.stringifyObjectsInQueryObject(
            companyVendorQueryObj
          )
        ).subscribe({
          next: (CompanyVendor: CompanyVendor) => {
            if (!CompanyVendor) {
              throw this.ToastService.addErrorToast(
                'Error',
                'No CompanyVendor Record found.',
                { returnError: true }
              );
            }

            this.CompanyVendor = CompanyVendor;
            const {
              isOwnerInfoRequired,
              registrationType,
              country,
              CompanyComplianceTypeId,
              Vendor: VendorRecord,
              ClientRegisteredVendor: ClientRegisteredVendorRecord,
              ExemptVendor: ExemptVendorRecord,
              InsuranceCertificates,
              WSIB,
              WCB,
              Company,
              TradeLicenseComplianceProgress,
              vendorApprovedTradeStates
            } = CompanyVendor;

            Object.assign(this, {
              registrationType,
              TradeLicenseComplianceProgress
            });

            if (!isOwnerInfoRequired) {
              this.excludedRegistrationSteps.add('owners');
            }

            this.forceCompleteRegistrationSteps.clear();
            if (
              TradeLicenseComplianceProgress?.subStatus === 'No Licensed Trades'
            ) {
              this.forceCompleteRegistrationSteps.add('licenses');
            }

            if (country) {
              this.localizationInfo =
                this.UtilityService.getLocalizedValuesMap(country);
            } else {
              throw this.ToastService.addErrorToast(
                'Error',
                'Country is not supported.',
                { returnError: true }
              );
            }

            if (!CompanyComplianceTypeId) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Compliance Type Id.',
                { returnError: true }
              );
            }

            if (!Company) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing client info.',
                { returnError: true }
              );
            }

            // Resolve Vendor before compliance gates — empty CCT fixtures (Test 7)
            // may lack a matching ComplianceSetting and must still show identity fields.
            if (VendorRecord) {
              this.Vendor = new Vendor(VendorRecord);
              const { LogoFileId } = this.Vendor;

              if (LogoFileId) {
                this.VendorService.getVendorLogoS3PresignedUrl(
                  this.Vendor.id!
                ).subscribe({
                  next: (s3Response) => {
                    this.logoUrl = s3Response.presignedUrl;
                    this._cdr.markForCheck();
                  }
                });
              }
            } else if (ExemptVendorRecord) {
              const ExemptVendorInstance = new ExemptVendor(ExemptVendorRecord);
              this.Vendor = ExemptVendorInstance;
              const { ExemptVendorTypeId } = ExemptVendorInstance;
              if (
                ExemptVendorTypeId &&
                this.canViewVendorTierComplianceType()
              ) {
                this.setExemptVendorTypeName(ExemptVendorTypeId);
              }
            } else if (ClientRegisteredVendorRecord) {
              this.Vendor = new ClientRegisteredVendor(
                ClientRegisteredVendorRecord
              );
            }

            if (this.Vendor) {
              const { Contacts, VendorTrades, VendorRankings } = this.Vendor;
              if (Contacts && Contacts.length > 0) {
                this.primaryContact =
                  Contacts.find((c) => c.isPrimaryCompliance) ||
                  first(Contacts);
              }
              if (VendorTrades && VendorTrades.length > 0) {
                const { tradesConcatenated } =
                  this.UtilityService.generateConcatenatedTradesAndTradeCategories(
                    VendorTrades
                  );
                this.tradesConcatenated = tradesConcatenated;
              }
              if (VendorRankings) {
                this.preferredStatus = first(VendorRankings)
                  ?.CompanyServiceRanking?.rank as string;
              }
              if (VendorId && vendorApprovedTradeStates) {
                this.vendorTradeStatesConcatenated = vendorApprovedTradeStates;
              }
            }

            const {
              BusinessComplianceSettings,
              ComplianceSettings,
              hasGlobalCompliance,
              CompanyComplianceTypes
            } = Company;
            this.usesRiskTiers = hasGlobalCompliance === false;
            this.hasComplianceTypes = !!(
              CompanyComplianceTypes && CompanyComplianceTypes.length > 0
            );
            this.usesSpotlightVendors = false;
            if (this.AuthService.isClient()) {
              // Not the companyServiceRanking list: that route admits only
              // CLIENT_ADMIN among client roles, so it 401s for a Manager.
              from(
                spotlightReportsService.usesSpotlightVendors({
                  pathParams: { companyId: CompanyId },
                  options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
                })
              ).subscribe({
                next: ({ usesSpotlightVendors }) => {
                  this.usesSpotlightVendors = usesSpotlightVendors;
                  this._cdr.markForCheck();
                },
                error: () => {
                  this.usesSpotlightVendors = false;
                }
              });
            }
            // Need to remove any business compliance settings that don't apply to the vendor
            if (BusinessComplianceSettings) {
              remove(BusinessComplianceSettings, (setting) => {
                return (
                  setting.CompanyComplianceTypeId !== CompanyComplianceTypeId
                );
              });
            }

            if (!Array.isArray(ComplianceSettings)) {
              this.ToastService.addErrorToast(
                'Error',
                'Missing Compliance Settings.'
              );
              this._cdr.markForCheck();
              return;
            }

            this.ComplianceSetting = ComplianceSettings.find(
              (cs) => cs.CompanyComplianceTypeId === CompanyComplianceTypeId
            );

            if (!this.ComplianceSetting) {
              this.ToastService.addErrorToast(
                'Error',
                'Compliance Setting was incorrectly loaded.'
              );
              this._cdr.markForCheck();
              return;
            }

            if (!Array.isArray(BusinessComplianceSettings)) {
              this.ToastService.addErrorToast(
                'Error',
                'Missing Business Compliance Settings.'
              );
              this._cdr.markForCheck();
              return;
            }

            if (BusinessComplianceSettings.length > 1) {
              this.ToastService.addErrorToast(
                'Error',
                'Business Compliance Setting was incorrectly loaded.'
              );
              this._cdr.markForCheck();
              return;
            }

            this.BusinessComplianceSetting = BusinessComplianceSettings[0];

            if (Array.isArray(InsuranceCertificates)) {
              InsuranceCertificates.forEach((InsuranceCertificate) => {
                const { id: InsuranceCertificateId, InsurancePolicies } =
                  InsuranceCertificate;

                if (!InsurancePolicies) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Insurance certificate is missing insurance policies.',
                    { returnError: true }
                  );
                }

                if (!InsuranceCertificateId) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Insurance Certificate is missing Id.',
                    { returnError: true }
                  );
                }

                InsurancePolicies.forEach((InsurancePolicy) => {
                  const { policyType } = InsurancePolicy;

                  if (!policyType) {
                    throw this.ToastService.addErrorToast(
                      'Error',
                      'Insurance policy is missing policy type.',
                      { returnError: true }
                    );
                  }

                  this.InsurancePolicyMap.set(policyType, InsurancePolicy);
                });

                this.InsuranceCertificatesMappedById.set(
                  InsuranceCertificateId,
                  InsuranceCertificate
                );
              });
            }

            if (this.Vendor) {
              const { Contacts, VendorTrades, VendorRankings } = this.Vendor;

              if (Contacts && Contacts.length > 0) {
                this.primaryContact =
                  Contacts.find((c) => c.isPrimaryCompliance) ||
                  first(Contacts);
              }

              if (VendorTrades && VendorTrades.length > 0) {
                const { tradesConcatenated } =
                  this.UtilityService.generateConcatenatedTradesAndTradeCategories(
                    VendorTrades
                  );
                this.tradesConcatenated = tradesConcatenated;
              }

              if (VendorRankings) {
                this.preferredStatus = first(VendorRankings)
                  ?.CompanyServiceRanking?.rank as string;
              }

              if (VendorId && this.Vendor instanceof Vendor) {
                const { InsuranceAgents } = this.Vendor;

                if (vendorApprovedTradeStates) {
                  this.vendorTradeStatesConcatenated =
                    vendorApprovedTradeStates;
                }

                this.ComplianceService.getComplianceResults(
                  CompanyId,
                  VendorId
                ).subscribe({
                  next: (ComplianceResults) => {
                    const { InsuranceCertificateFindingResults } =
                      ComplianceResults;

                    this.recalculateComplianceResults(ComplianceResults);
                    this.InsuranceCertificateFindingResults =
                      InsuranceCertificateFindingResults;
                    this.settingsAndFindingsResults = ComplianceResults;

                    this.generalLiabilities = [
                      {
                        title: 'AM Best Rating',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .generalLiability_amBestRating,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .generalLiability_amBestRating,
                        result:
                          this.settingsAndFindingsResults
                            .generalLiability_amBestRating,
                        enabled: true
                      },
                      {
                        title: 'Policy Per Occurrence Limit',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .generalLiability_policyAmountPerOccurance,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .generalLiability_policyAmountPerOccurance,
                        result:
                          this.settingsAndFindingsResults
                            .generalLiability_policyAmountPerOccurance,
                        enabled: true
                      },
                      {
                        title: 'Damage to Rented Premises',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .generalLiability_damageToRentedPremises,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .generalLiability_damageToRentedPremises,
                        result:
                          this.settingsAndFindingsResults
                            .generalLiability_damageToRentedPremises,
                        enabled: true
                      },
                      {
                        title: 'Medical Expenses',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .generalLiability_medicalExpenses,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .generalLiability_medicalExpenses,
                        result:
                          this.settingsAndFindingsResults
                            .generalLiability_medicalExpenses,
                        enabled: true
                      },
                      {
                        title: 'Personal & Advertisement Injury',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .personalAdvertisementInjury,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .personalAdvertisementInjury,
                        result:
                          this.settingsAndFindingsResults
                            .personalAdvertisementInjury,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings
                            .personalAdvertisementInjury ||
                          this.settingsAndFindingsResults.findings
                            .personalAdvertisementInjury
                        )
                      },
                      {
                        title: 'Policy Amount Aggregate',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .generalLiability_policyAmountAggregate,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .generalLiability_policyAmountAggregate,
                        result:
                          this.settingsAndFindingsResults
                            .generalLiability_policyAmountAggregate,
                        enabled: true
                      },
                      {
                        title: 'Prod-Comp/Op Aggregate',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .generalLiability_productOps,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .generalLiability_productOps,
                        result:
                          this.settingsAndFindingsResults
                            .generalLiability_productOps,
                        enabled: true
                      },
                      {
                        title: 'Additional Insured',
                        finding: this.settingsAndFindingsResults.findings
                          .glAddlInsured
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .glIsNamed
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result: this.settingsAndFindingsResults.glIsNamed,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings.glIsNamed ||
                          this.settingsAndFindingsResults.findings.glAddlInsured
                        )
                      },
                      {
                        title: 'Waiver of Subrogation Checked?',
                        finding: this.settingsAndFindingsResults.findings
                          .glsubrWvd
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .glIsAgency
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result: this.settingsAndFindingsResults.glIsAgency,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings.glIsAgency ||
                          this.settingsAndFindingsResults.findings.glsubrWvd
                        )
                      },
                      {
                        title: 'Description of Operations Validated?',
                        finding: this.settingsAndFindingsResults.findings
                          .glHasDescOps
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .glDescOps
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults
                            .generalLiability_hasDescOps,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings.glDescOps ||
                          this.settingsAndFindingsResults.findings.glHasDescOps
                        )
                      },
                      {
                        title: 'A.I. Ongoing Ops?',
                        finding: this.settingsAndFindingsResults.findings
                          .glHasAiOngoingOps
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .generalLiability_hasAiOngoingOps
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults
                            .generalLiability_hasAiOngoingOps,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings
                            .generalLiability_hasAiOngoingOps ||
                          this.settingsAndFindingsResults.findings
                            .glHasAiOngoingOps
                        )
                      },
                      {
                        title: 'A.I. Comp. Ops?',
                        finding: this.settingsAndFindingsResults.findings
                          .glHasAiCompOps
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .generalLiability_hasAiCompOps
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults
                            .generalLiability_hasAiCompOps,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings
                            .generalLiability_hasAiCompOps ||
                          this.settingsAndFindingsResults.findings
                            .glHasAiCompOps
                        )
                      },
                      {
                        title: 'Primary and Non-Contributory (PNC)?',
                        finding: this.settingsAndFindingsResults.findings
                          .glHasPnc
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .generalLiability_hasPnc
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults
                            .generalLiability_hasPnc,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings
                            .generalLiability_hasPnc ||
                          this.settingsAndFindingsResults.findings.glHasPnc
                        )
                      },
                      {
                        title: 'Contractual Liability?',
                        finding: this.settingsAndFindingsResults.findings
                          .glHasPolicyDoc
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .generalLiability_contractualLiability
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults
                            .generalLiability_contractualLiability,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings
                            .generalLiability_contractualLiability ||
                          this.settingsAndFindingsResults.findings
                            .glHasPolicyDoc
                        )
                      },
                      {
                        title: 'Has Passed Full Policy Review?',
                        finding: this.settingsAndFindingsResults.findings
                          .glIsContractualLiability
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .generalLiability_hasFullPolicyDoc
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults
                            .generalLiability_hasFullPolicyDoc,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings
                            .generalLiability_hasFullPolicyDoc ||
                          this.settingsAndFindingsResults.findings
                            .glIsContractualLiability
                        )
                      },
                      {
                        title: 'HNO Auto Coverage',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .glNonOwnedAutoAmount,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .generalLiability_nonOwnedAutoAmount,
                        result:
                          this.settingsAndFindingsResults
                            .generalLiability_nonOwnedAutoAmount,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings
                            .generalLiability_nonOwnedAutoAmount ||
                          this.settingsAndFindingsResults.findings
                            .glNonOwnedAutoAmount
                        )
                      },
                      {
                        title: 'Has Required Forms?',
                        finding: this.settingsAndFindingsResults.findings
                          .generalLiability_hasRequiredForms
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .generalLiability_hasRequiredForms
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults
                            .generalLiability_hasRequiredForms,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings
                            .generalLiability_hasRequiredForms ||
                          this.settingsAndFindingsResults.findings
                            .generalLiability_hasRequiredForms
                        )
                      },
                      {
                        title: 'Occurrence based?',
                        finding: this.settingsAndFindingsResults.findings
                          .glIsPerOccurrence
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .generalLiability_isPerOccurrence
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults
                            .generalLiability_isPerOccurrence,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings
                            .generalLiability_isPerOccurrence ||
                          this.settingsAndFindingsResults.findings
                            .glIsPerOccurrence
                        )
                      },
                      {
                        title: 'Per Proj/Loc Agg?',
                        finding: this.settingsAndFindingsResults.findings
                          .glPerProjLocAgg
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .generalLiability_perProjLocAgg
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result: this.settingsAndFindingsResults.glPerProjLocAgg,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings
                            .generalLiability_perProjLocAgg ||
                          this.settingsAndFindingsResults.findings
                            .glPerProjLocAgg
                        )
                      },
                      {
                        title:
                          'Additional Insured Endorsement-Scheduled names?',
                        finding: this.settingsAndFindingsResults.findings
                          .glHasAdditionalInsuredEndorsements
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .glAdditionalInsuredEndorsements
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults
                            .glAdditionalInsuredEndorsements,
                        enabled:
                          this.settingsAndFindingsResults.settings
                            .glAdditionalInsuredEndorsements ||
                          this.settingsAndFindingsResults.findings
                            .glHasAdditionalInsuredEndorsements
                      },
                      {
                        title: 'COI Name/Legal Name Mismatch?',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .generalLiability_legalNameMismatch,
                        setting: this.settingsAndFindingsResults.settings
                          .generalLiability_legalNameMismatch
                          ? 'No'
                          : 'Yes',
                        result:
                          this.settingsAndFindingsResults
                            .generalLiability_legalNameMismatch,
                        enabled:
                          this.settingsAndFindingsResults.findings
                            .generalLiability_legalNameMismatch ||
                          this.settingsAndFindingsResults.settings
                            .generalLiability_legalNameMismatch
                      }
                    ];

                    this.autoLiabilities = [
                      {
                        title: 'Is Auto a Commercial Policy?',
                        finding: this.settingsAndFindingsResults.findings
                          .allowNonCommercialPolicy
                          ? 'Yes'
                          : 'No',
                        setting:
                          this.settingsAndFindingsResults.settings
                            .allowNonCommercialPolicy ||
                          this.settingsAndFindingsResults.settings
                            .autoLiability_isRequired
                            ? 'REQUIRED'
                            : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults
                            .allowNonCommercialPolicy,
                        enabled: true
                      },
                      {
                        title: 'AM Best Rating',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .autoLiability_amBestRating,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .autoLiability_amBestRating,
                        result:
                          this.settingsAndFindingsResults
                            .autoLiability_amBestRating,
                        enabled: true
                      },
                      {
                        title: 'AM Best Financial Size Category',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .autoLiability_financialSizeCategory,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .autoLiability_financialSizeCategory,
                        result:
                          this.settingsAndFindingsResults
                            .autoLiability_financialSizeCategory,
                        enabled: true
                      },
                      {
                        title: 'Combined Single Limit',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .autoLiability_combinedSingleLimit,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .autoLiability_combinedSingleLimit,
                        result:
                          this.settingsAndFindingsResults
                            .autoLiability_combinedSingleLimit,
                        enabled: true
                      },
                      {
                        title: 'Bodily Injury Per Person',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .autoLiability_bodilyInjuryPerPerson,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .autoLiability_bodilyInjuryPerPerson,
                        result:
                          this.settingsAndFindingsResults
                            .autoLiability_bodilyInjuryPerPerson,
                        enabled: true
                      },
                      {
                        title: 'Injury Per Accident',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .autoLiability_bodilyInjuryPerAccident,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .autoLiability_bodilyInjuryPerAccident,
                        result:
                          this.settingsAndFindingsResults
                            .autoLiability_bodilyInjuryPerAccident,
                        enabled: true
                      },
                      {
                        title: 'Property Damage Limit',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .autoLiability_propertyDamageLimit,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .autoLiability_propertyDamageLimit,
                        result:
                          this.settingsAndFindingsResults
                            .autoLiability_propertyDamageLimit,
                        enabled: true
                      },
                      {
                        title:
                          'Additional Insured\n(Primary/Non-Contributory Basis)?',
                        finding: this.settingsAndFindingsResults.findings
                          .autoAddlInsured
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .autoIsAgency
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result: this.settingsAndFindingsResults.autoIsNamed,
                        enabled:
                          this.settingsAndFindingsResults.settings
                            .autoIsNamed ||
                          this.settingsAndFindingsResults.findings
                            .autoAddlInsured
                      },
                      {
                        title: 'Waiver of Subrogation Checked?',
                        finding: this.settingsAndFindingsResults.findings
                          .autosubrWvd
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .autoIsAgency
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result: this.settingsAndFindingsResults.autoIsAgency,
                        enabled:
                          this.settingsAndFindingsResults.settings
                            .autoIsAgency ||
                          this.settingsAndFindingsResults.findings.autosubrWvd
                      },
                      {
                        title: 'Description of Operations Validated?',
                        finding: this.settingsAndFindingsResults.findings
                          .autoHasDescOps
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .autoDescOps
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults
                            .autoLiability_hasDescOps,
                        enabled:
                          this.settingsAndFindingsResults.settings
                            .autoDescOps ||
                          this.settingsAndFindingsResults.findings
                            .autoHasDescOps
                      },
                      {
                        title: 'Hired & Non-Owned Auto Coverage?',
                        finding: this.settingsAndFindingsResults.findings
                          .autoHasNonOwnedAuto
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .autoLiability_hasNonOwnedAuto
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults.autoHasNonOwnedAuto,
                        enabled:
                          this.settingsAndFindingsResults.settings
                            .autoLiability_hasNonOwnedAuto ||
                          this.settingsAndFindingsResults.findings
                            .autoHasNonOwnedAuto
                      },
                      {
                        title: 'Has Passed Full Policy Review?',
                        finding: this.settingsAndFindingsResults.findings
                          .autoHasPolicyDoc
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .autoLiability_hasFullPolicyDoc
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults
                            .autoLiability_hasPolicyDoc,
                        enabled:
                          this.settingsAndFindingsResults.settings
                            .autoLiability_hasFullPolicyDoc ||
                          this.settingsAndFindingsResults.findings
                            .autoHasPolicyDoc
                      },
                      {
                        title: 'Has Required Forms?',
                        finding: this.settingsAndFindingsResults.findings
                          .autoLiability_hasRequiredForms
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .autoLiability_hasRequiredForms
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults
                            .autoLiability_hasRequiredForms,
                        enabled:
                          this.settingsAndFindingsResults.settings
                            .autoLiability_hasRequiredForms ||
                          this.settingsAndFindingsResults.findings
                            .autoLiability_hasRequiredForms
                      },
                      {
                        title:
                          'Additional Insured Endorsement-Scheduled names?',
                        finding: this.settingsAndFindingsResults.findings
                          .autoHasAdditionalInsuredEndorsements
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .autoAdditionalInsuredEndorsements
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults
                            .autoHasAdditionalInsuredEndorsements,
                        enabled:
                          this.settingsAndFindingsResults.settings
                            .autoAdditionalInsuredEndorsements ||
                          this.settingsAndFindingsResults.findings
                            .autoHasAdditionalInsuredEndorsements
                      },
                      {
                        title: 'COI Name/Legal Name Mismatch?',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .autoLiability_legalNameMismatch,
                        setting: this.settingsAndFindingsResults.settings
                          .autoLiability_legalNameMismatch
                          ? 'No'
                          : 'Yes',
                        result:
                          this.settingsAndFindingsResults
                            .autoLiability_legalNameMismatch,
                        enabled:
                          this.settingsAndFindingsResults.findings
                            .autoLiability_legalNameMismatch ||
                          this.settingsAndFindingsResults.settings
                            .autoLiability_legalNameMismatch
                      }
                    ];

                    this.umbrellaLiabilities = [
                      {
                        title: 'AM Best Rating',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .umbrellaLiability_amBestRating,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .umbrellaLiability_amBestRating,
                        result:
                          this.settingsAndFindingsResults
                            .umbrellaLiability_amBestRating,
                        enabled: true
                      },
                      {
                        title: 'AM Best Financial Size Category',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .umbrellaLiability_financialSizeCategory,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .umbrellaLiability_financialSizeCategory,
                        result:
                          this.settingsAndFindingsResults
                            .umbrellaLiability_financialSizeCategory,
                        enabled: true
                      },
                      {
                        title: 'Policy Amount Per Occurrence',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .umbrellaLiability_policyAmountPerOccurance,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .umbrellaLiability_policyAmountPerOccurance,
                        result:
                          this.settingsAndFindingsResults
                            .umbrellaLiability_policyAmountPerOccurance,
                        enabled: true
                      },
                      {
                        title: 'Policy Amount Aggregate',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .umbrellaLiability_policyAmountAggregate,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .umbrellaLiability_policyAmountAggregate,
                        result:
                          this.settingsAndFindingsResults
                            .umbrellaLiability_policyAmountAggregate,
                        enabled: true
                      },
                      {
                        title: 'Additional Insured',
                        finding: this.settingsAndFindingsResults.findings
                          .ulAddlInsured
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .umbIsNamed
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result: this.settingsAndFindingsResults.umbIsNamed,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings.umbIsNamed ||
                          this.settingsAndFindingsResults.findings.ulAddlInsured
                        )
                      },
                      {
                        title: 'Waiver of Subrogation Checked?',
                        finding: this.settingsAndFindingsResults.findings
                          .ulSubrWvd
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .umbIsAgency
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result: this.settingsAndFindingsResults.umbIsAgency,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings
                            .umbIsAgency ||
                          this.settingsAndFindingsResults.findings.ulSubrWvd
                        )
                      },
                      {
                        title: 'Description of Operations Validated?',
                        finding: this.settingsAndFindingsResults.findings
                          .ulHasDescOps
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .umbrellaLiability_hasFullPolicyDoc
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result: this.settingsAndFindingsResults.umbDescOps,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings
                            .umbrellaLiability_hasFullPolicyDoc ||
                          this.settingsAndFindingsResults.findings.ulHasDescOps
                        )
                      },
                      {
                        title: 'Has Passed Full Policy Review?',
                        finding: this.settingsAndFindingsResults.findings
                          .ulHasPolicyDoc
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .umbDescOps
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults
                            .umbrellaLiability_hasDescOps,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings.umbDescOps ||
                          this.settingsAndFindingsResults.findings
                            .ulHasPolicyDoc
                        )
                      },
                      {
                        title: 'Has Required Forms?',
                        finding: this.settingsAndFindingsResults.findings
                          .umbrellaLiability_hasRequiredForms
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .umbrellaLiability_hasRequiredForms
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults
                            .umbrellaLiability_hasRequiredForms,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings
                            .umbrellaLiability_hasRequiredForms ||
                          this.settingsAndFindingsResults.findings
                            .umbrellaLiability_hasRequiredForms
                        )
                      },
                      {
                        title: 'Primary & Non-Contributory?',
                        finding: this.settingsAndFindingsResults.findings
                          .ulPrimaryNonContributory
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .umbrellaLiability_primaryNonContributory
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults
                            .umbPrimaryNonContributory,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings
                            .umbrellaLiability_primaryNonContributory ||
                          this.settingsAndFindingsResults.findings
                            .ulPrimaryNonContributory
                        )
                      },
                      {
                        title: 'Occurrence Based?',
                        finding: this.settingsAndFindingsResults.findings
                          .ulOccurrenceBased
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .umbrellaLiability_occurrenceBased
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result:
                          this.settingsAndFindingsResults.umbOccurrenceBased,
                        enabled: Boolean(
                          this.settingsAndFindingsResults.settings
                            .umbrellaLiability_occurrenceBased ||
                          this.settingsAndFindingsResults.findings
                            .ulOccurrenceBased
                        )
                      },
                      {
                        title: 'COI Name/Legal Name Mismatch?',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .umbrellaLiability_legalNameMismatch,
                        setting: this.settingsAndFindingsResults.settings
                          .umbrellaLiability_legalNameMismatch
                          ? 'No'
                          : 'Yes',
                        result:
                          this.settingsAndFindingsResults
                            .umbrellaLiability_legalNameMismatch,
                        enabled:
                          this.settingsAndFindingsResults.findings
                            .umbrellaLiability_legalNameMismatch ||
                          this.settingsAndFindingsResults.settings
                            .umbrellaLiability_legalNameMismatch
                      }
                    ];

                    if (country === 'CA') {
                      const CanadianWorkersComp = WSIB || WCB;

                      if (!CanadianWorkersComp) {
                        throw this.ToastService.addErrorToast(
                          'Error',
                          'Missing canadian workers comp.',
                          { returnError: true }
                        );
                      }

                      const { finding, result } =
                        this.ComplianceService.calculateCanadianCompensationFindingAndResult(
                          CanadianWorkersComp,
                          this.settingsAndFindingsResults.settings
                            .workersCompensation_isRequired
                        );

                      const { type } = CanadianWorkersComp;

                      this.workersComp = [
                        {
                          title: `${type} Coverage passes all client requirements?`,
                          finding,
                          setting: this.settingsAndFindingsResults.settings
                            .workersCompensation_isRequired
                            ? 'REQUIRED'
                            : 'NOT REQUIRED',
                          result,
                          enabled: true
                        }
                      ];
                    } else {
                      this.workersComp = [
                        {
                          title: 'AM Best Rating',
                          finding:
                            this.settingsAndFindingsResults.findings
                              .workersCompensation_amBestRating,
                          setting:
                            this.settingsAndFindingsResults.settings
                              .workersCompensation_amBestRating,
                          result:
                            this.settingsAndFindingsResults
                              .workersCompensation_amBestRating,
                          enabled: true
                        },
                        {
                          title: 'AM Financial Size Category',
                          finding:
                            this.settingsAndFindingsResults.findings
                              .workersCompensation_financialSizeCategory,
                          setting:
                            this.settingsAndFindingsResults.settings
                              .workersCompensation_financialSizeCategory,
                          result:
                            this.settingsAndFindingsResults
                              .workersCompensation_financialSizeCategory,
                          enabled: true
                        },
                        {
                          title: 'E.L. Each Accident',
                          finding:
                            this.settingsAndFindingsResults.findings
                              .workersCompensation_eachIncidentEmployersLimit,
                          setting:
                            this.settingsAndFindingsResults.settings
                              .workersCompensation_eachIncidentEmployersLimit,
                          result:
                            this.settingsAndFindingsResults
                              .workersCompensation_eachIncidentEmployersLimit,
                          enabled: true
                        },
                        {
                          title: 'E.L. Disease - EA Employee',
                          finding:
                            this.settingsAndFindingsResults.findings
                              .workersCompensation_eachEmployeeMinimum,
                          setting:
                            this.settingsAndFindingsResults.settings
                              .workersCompensation_eachEmployeeMinimum,
                          result:
                            this.settingsAndFindingsResults
                              .workersCompensation_eachEmployeeMinimum,
                          enabled: true
                        },
                        {
                          title: 'E.L. Disease - Policy Limit',
                          finding:
                            this.settingsAndFindingsResults.findings
                              .workersCompensation_eachEmployerMinimum,
                          setting:
                            this.settingsAndFindingsResults.settings
                              .workersCompensation_eachEmployerMinimum,
                          result:
                            this.settingsAndFindingsResults
                              .workersCompensation_eachEmployerMinimum,
                          enabled: true
                        },
                        {
                          title: 'Waiver of Subrogation Checked?',
                          finding: this.settingsAndFindingsResults.findings
                            .wcSubrWvd
                            ? 'YES'
                            : 'NO',
                          setting: this.settingsAndFindingsResults.settings
                            .wkIsAgency
                            ? 'REQUIRED'
                            : 'OPTIONAL',
                          result: this.settingsAndFindingsResults.wkIsAgency,
                          enabled: Boolean(
                            this.settingsAndFindingsResults.settings
                              .wkIsAgency ||
                            this.settingsAndFindingsResults.findings.wkIsAgency
                          )
                        },
                        {
                          title: 'Description of Operations Validated?',
                          finding: this.settingsAndFindingsResults.findings
                            .wcHasDescOps
                            ? 'YES'
                            : 'NO',
                          setting: this.settingsAndFindingsResults.settings
                            .wkDescOps
                            ? 'REQUIRED'
                            : 'OPTIONAL',
                          result:
                            this.settingsAndFindingsResults
                              .workersCompensation_hasDescOps,
                          enabled: Boolean(
                            this.settingsAndFindingsResults.settings
                              .wkDescOps ||
                            this.settingsAndFindingsResults.findings
                              .wcHasDescOps
                          )
                        },
                        {
                          title: 'Has Required Forms?',
                          finding: this.settingsAndFindingsResults.findings
                            .workersCompensation_hasRequiredForms
                            ? 'YES'
                            : 'NO',
                          setting: this.settingsAndFindingsResults.settings
                            .workersCompensation_hasRequiredForms
                            ? 'REQUIRED'
                            : 'OPTIONAL',
                          result:
                            this.settingsAndFindingsResults
                              .workersCompensation_hasRequiredForms,
                          enabled: Boolean(
                            this.settingsAndFindingsResults.settings
                              .workersCompensation_hasRequiredForms ||
                            this.settingsAndFindingsResults.findings
                              .workersCompensation_hasRequiredForms
                          )
                        },
                        {
                          title: 'Officer Exclusion?',
                          finding: this.settingsAndFindingsResults.findings
                            .workersCompensation_officerExcluded
                            ? 'YES'
                            : 'NO',
                          setting: this.settingsAndFindingsResults.settings
                            .workersCompensation_officerExclusionNotAllowed
                            ? 'NOT ALLOWED'
                            : 'ALLOWED',
                          result:
                            this.settingsAndFindingsResults
                              .workersCompensation_officerExcluded,
                          enabled: Boolean(
                            this.settingsAndFindingsResults.settings
                              .workersCompensation_officerExclusionNotAllowed ||
                            this.settingsAndFindingsResults.findings
                              .workersCompensation_officerExcluded
                          )
                        },
                        {
                          title: 'COI Name/Legal Name Mismatch?',
                          finding:
                            this.settingsAndFindingsResults.findings
                              .workersCompensation_legalNameMismatch,
                          setting: this.settingsAndFindingsResults.settings
                            .workersCompensation_legalNameMismatch
                            ? 'No'
                            : 'Yes',
                          result:
                            this.settingsAndFindingsResults
                              .workersCompensation_legalNameMismatch,
                          enabled:
                            this.settingsAndFindingsResults.findings
                              .workersCompensation_legalNameMismatch ||
                            this.settingsAndFindingsResults.settings
                              .workersCompensation_legalNameMismatch
                        }
                      ];
                    }

                    this.professionalLiabilities = [
                      {
                        title: 'AM Best Rating',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .errorsAndOmissions_amBestRating,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .errorsAndOmissions_amBestRating,
                        result:
                          this.settingsAndFindingsResults
                            .errorsAndOmissions_amBestRating,
                        enabled: true
                      },
                      {
                        title: 'AM Best Financial Size Category',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .plFinancialSizeCategory,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .errorsAndOmissions_financialSizeCategory,
                        result:
                          this.settingsAndFindingsResults
                            .errorsAndOmissions_financialSizeCategory,
                        enabled: true
                      },
                      {
                        title: 'Each Occurrence',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .errorsAndOmissions_policyAmount,
                        setting:
                          this.settingsAndFindingsResults.settings
                            .errorsAndOmissions_policyAmount,
                        result:
                          this.settingsAndFindingsResults
                            .errorsAndOmissions_policyAmount,
                        enabled: true
                      },
                      {
                        title: 'Additional Insured',
                        finding: this.settingsAndFindingsResults.findings
                          .plAddlInsured
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .errIsNamed
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result: this.settingsAndFindingsResults.errIsNamed,
                        enabled: true
                      },
                      {
                        title: 'Waiver of Subrogation',
                        finding: this.settingsAndFindingsResults.findings
                          .plsubrWvd
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .errIsAgency
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result: this.settingsAndFindingsResults.errIsAgency,
                        enabled: true
                      },
                      {
                        title: 'Valid Description of Operations',
                        finding: this.settingsAndFindingsResults.findings
                          .plHasDescOps
                          ? 'Yes'
                          : 'No',
                        setting: this.settingsAndFindingsResults.settings
                          .errDescOps
                          ? 'REQUIRED'
                          : 'OPTIONAL',
                        result: this.settingsAndFindingsResults.errDescOps,
                        enabled: true
                      },
                      {
                        title: 'COI Name/Legal Name Mismatch',
                        finding:
                          this.settingsAndFindingsResults.findings
                            .professionalLiability_legalNameMismatch,
                        setting: this.settingsAndFindingsResults.settings
                          .professionalLiability_legalNameMismatch
                          ? 'No'
                          : 'Yes',
                        result:
                          this.settingsAndFindingsResults
                            .professionalLiability_legalNameMismatch,
                        enabled: true
                      }
                    ];
                    this.appendCertificateFindingsToPolicies();
                    this._cdr.markForCheck();
                  }
                });
              }
            }
            this._cdr.markForCheck();
          }
        });
      }
    });
  }
}
